import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved -- pre-existing; numeral resolves via pnpm at build/runtime
import numeral from 'numeral';
import { CannabinoidAbbrev } from 'shared/constants/cannabinoids';
import { NUMERALS_DOLLAR_FORMAT } from 'shared/constants';

function extractValidationErrors(graphQLErrors) {
  const errors = _.filter(
    graphQLErrors,
    (e) => e.extensions.code === 'BAD_USER_INPUT' || e.extensions.code === 'UNPROCESSABLE_ENTITY'
  );
  return _.flatMap(errors, (error) => _.map(error.extensions.errors, 'detail'));
}

const formatPrice = (price) => numeral(parseFloat(price).toFixed(2)).format(NUMERALS_DOLLAR_FORMAT);

const formatFromCents = (priceInCents) => formatPrice(priceInCents / 100);

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

function getBufferFromBase64Image(image = '') {
  return Buffer.from(image.replace(/^data:image\/\w+;base64,/, ''), 'base64');
}

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.document !== 'undefined';
}

// Check if an elem is visible on screen, and whether its above or below a certain offset
function isScrolledIntoView(elem, threshold = 0, mode = 'visible') {
  const rect = elem.getBoundingClientRect();
  const viewHeight = Math.max(document.documentElement.clientHeight, window.innerHeight);
  const above = rect.bottom - threshold < 0;
  const below = rect.top - viewHeight + threshold >= 0;

  if (mode === 'above') {
    return above;
  }
  if (mode === 'below') {
    return below;
  }
  return !above && !below;
}

// Extracts visible plain text from an HTML string for "is this blank?" checks
// (e.g. a menu banner of `<p>&nbsp;</p>` should count as empty). Implemented without
// DOMParser so it produces the same result during SSR (Node, where DOMParser doesn't
// exist) and on the client — using a real DOM parser only in the browser made the
// value differ between the server render and the client's first render.
export function getHtmlPlainText(html) {
  if (!html || typeof html !== 'string') {
    return '';
  }
  return html
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '') // drop script/style tags and their contents
    .replace(/<[^>]+>/g, '') // strip remaining tags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&'); // decode ampersand last to avoid double-decoding
}

// normalizes noid abbrev, like THC-D9 -> D9-THC
const mapToStandardAbbreviation = (abbrev) => CannabinoidAbbrev[abbrev];

// Extracts noid abbrev from full text
// ex. '(CBDA) Cannablahblah' to 'CBDA' or '"TAC" - Total Noids' to 'TAC'
export const parseNoidAbbreviation = (noidName) => mapToStandardAbbreviation(/[\w-]+/.exec(noidName)?.[0]) ?? noidName;

// Extracts noid long name from full text
// ex. '(CBDA) Cannablahblah' to 'Cannablahblah' or '"TAC" - Total Noids' to 'Total Noids'
export const parseNoidLongName = (noidName) => /(?:\(|\W\s)+(.+\b)(?:\)|$)/.exec(noidName)?.[1] ?? noidName;

export default {
  extractValidationErrors,
  formatFromCents,
  formatPrice,
  getBufferFromBase64Image,
  getDisplayName,
  isBrowser,
  isScrolledIntoView,
};
